import { BrowserCacheLocation, LogLevel } from '@azure/msal-browser';
import type { Configuration, PopupRequest, SilentRequest } from '@azure/msal-browser';
import { AZURE_AD_CLIENT_ID, AZURE_AD_TENANT } from './config';

/**
 * The sign-in popup and the hidden silent-refresh iframe both land here.
 * It is a static page rather than the SPA so authenticating never boots a
 * second copy of the app. Must match the redirect URI registered in Entra ID.
 */
const redirectUri = `${window.location.origin}/redirect.html`;

export const msalConfig: Configuration = {
  auth: {
    clientId: AZURE_AD_CLIENT_ID,
    authority: AZURE_AD_TENANT,
    redirectUri,
    postLogoutRedirectUri: `${window.location.origin}/dashboard`,
    navigateToLoginRequestUrl: false,
  },

  cache: {
    /**
     * localStorage keeps the refresh token across reloads and tabs. With
     * sessionStorage every new tab and every hard refresh started from an empty
     * cache, which is what surfaced as "randomly logged out".
     */
    cacheLocation: BrowserCacheLocation.LocalStorage,
  },

  system: {
    // Only reached once the refresh token itself has expired.
    iframeHashTimeout: 30000,
    loadFrameTimeout: 30000,
    loggerOptions: {
      logLevel: LogLevel.Error,
      piiLoggingEnabled: false,
      loggerCallback: (level, message, containsPii) => {
        if (containsPii || level !== LogLevel.Error) return;
        console.error('[msal]', message);
      },
    },
  },
};

/**
 * Resource scope for the platform gateway. `/.default` asks for every
 * permission already consented for this app registration.
 */
export const API_SCOPE = `${AZURE_AD_CLIENT_ID}/.default`;

/**
 * Interactive sign-in. MSAL appends `openid`, `profile` and `offline_access`
 * itself, so the authorization code exchange always returns a refresh token.
 */
export const loginRequest: PopupRequest = {
  scopes: ['User.Read'],
};

/** Refresh-token backed acquisition of the gateway access token. */
export const tokenRequest: SilentRequest = {
  scopes: [API_SCOPE],
};
